import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { apiUrl } from '../../../auth/api-url';

interface CatalogApiItem {
  id?: number;
  name?: string;
  regionId?: number | null;
}

export interface RegionOption {
  id: number;
  name: string;
}

export interface CountryOption {
  id: number;
  name: string;
  regionId: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class CatalogLocationService {
  private readonly countryEndpoint = apiUrl('country');
  private readonly regionEndpoint = apiUrl('region');

  constructor(private readonly http: HttpClient) {}

  getCountries(): Observable<CountryOption[]> {
    return this.http.get<unknown>(this.countryEndpoint).pipe(
      map((response) => this.mapRows(response, (item) => ({
        ...this.toLookupOption(item),
        regionId: this.toNullableNumber(item.regionId),
      }))),
    );
  }

  getRegions(): Observable<RegionOption[]> {
    return this.http.get<unknown>(this.regionEndpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.toLookupOption(item))),
    );
  }

  private mapRows<T>(response: unknown, mapper: (item: CatalogApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private toLookupOption(item: CatalogApiItem): { id: number; name: string } {
    return {
      id: this.toNumber(item.id),
      name: this.toText(item.name),
    };
  }

  private extractRows(response: unknown): CatalogApiItem[] {
    if (Array.isArray(response)) {
      return response as CatalogApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as CatalogApiItem[];
      }

      if (Array.isArray(items)) {
        return items as CatalogApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }

  private toNullableNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }
}
